package er.prateek.crossly.game.crossword

import er.prateek.crossly.data.model.Clue
import er.prateek.crossly.data.model.Puzzle
import kotlin.math.roundToInt

/**
 * Picks the squares that open already filled in.
 *
 * A handful of free letters is the difference between an empty grid (which reads
 * as work) and a grid with a foothold (which reads as a puzzle already in
 * progress). Two rules keep them honest:
 *
 *  - the choice is seeded from the puzzle id, so re-opening a puzzle shows the
 *    same starters rather than re-rolling an easier board;
 *  - no word is ever handed over completely — a starter is dropped if it would
 *    finish an Across or Down answer on its own.
 *
 * Free letters are not hints: they cost the player nothing and never touch the
 * star rating.
 */
object StarterLetters {

    fun pick(puzzle: Puzzle, amount: StarterAmount): Set<CellPos> {
        if (amount == StarterAmount.NONE) return emptySet()

        val words = puzzle.cluesAcross.map { cellsOf(it, rowStep = 0, colStep = 1) } +
            puzzle.cluesDown.map { cellsOf(it, rowStep = 1, colStep = 0) }
        val playable = words.flatten().distinct()
        if (playable.isEmpty()) return emptySet()

        // At least one starter whenever the feature is on, and never so many that
        // the grid is more given than played.
        val target = (playable.size * amount.fraction).roundToInt()
            .coerceIn(1, (playable.size - 1).coerceAtLeast(1))

        val random = PuzzleRandom(puzzle.id.stableSeed())
        val given = mutableSetOf<CellPos>()
        for (pos in random.shuffled(playable)) {
            if (given.size >= target) break
            given += pos
            if (words.any { word -> given.containsAll(word) }) given -= pos
        }
        return given
    }

    private fun cellsOf(clue: Clue, rowStep: Int, colStep: Int): List<CellPos> =
        clue.answer.indices.map { i -> CellPos(clue.row + rowStep * i, clue.col + colStep * i) }
}
